<template>
  <div class="faq-page">
    <div class="world-map-bg"></div>
    <v-container>
      <div class="page-header text-center mb-10">
        <div class="global-badge mb-4">
          <v-icon class="globe-icon" icon="mdi-earth"></v-icon>
          <span>{{ $t('faq.international_support') }}</span>
        </div>
        <h1 class="page-title text-h4 text-md-h3 mb-4">{{ $t('faq.title') }}</h1>
        <v-divider class="mx-auto divider-primary mb-4" width="80"></v-divider>
        <p class="subtitle">{{ $t('faq.subtitle') }}</p>
      </div>

      <v-row>
        <v-col cols="12" md="8" class="mx-auto">
          <v-expansion-panels 
            variant="accordion"
            class="faq-panels"
          >
            <v-expansion-panel
              v-for="(faq, index) in faqs"
              :key="index"
              class="mb-4 faq-panel"
              rounded="lg"
              :style="{ 
                '--animation-delay': `${index * 0.1}s`,
                '--hover-color': `rgba(var(--v-theme-primary), ${0.05 + index * 0.01})`
              }"
            >
              <v-expansion-panel-title class="py-4">
                <div class="d-flex align-center panel-title-content">
                  <div class="icon-wrapper mr-4">
                    <v-icon 
                      color="primary" 
                      class="question-icon"
                      size="24"
                    >mdi-help-circle</v-icon>
                    <v-icon 
                      color="primary" 
                      class="answer-icon"
                      size="24"
                    >mdi-check-circle</v-icon>
                  </div>
                  <span class="text-subtitle-1 font-weight-medium panel-title">{{ faq.question }}</span>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="answer-content">
                  <div class="answer-wrapper">
                    <p class="text-body-1">{{ faq.answer }}</p>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <v-row class="mt-16">
        <v-col cols="12" class="text-center">
          <h2 class="text-h4 mb-8">{{ $t('faq.still_have_questions') }}</h2>
          <v-btn
            color="primary"
            size="large"
            rounded="pill"
            class="px-8 py-3"
            :to="{ name: 'contact' }"
          >
            <v-icon start>mdi-email</v-icon>
            {{ $t('faq.contact_us') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

// FAQ data
const faqs = computed(() => [
  {
    question: t('faq.questions.q1'),
    answer: t('faq.answers.a1')
  },
  {
    question: t('faq.questions.q2'),
    answer: t('faq.answers.a2')
  },
  {
    question: t('faq.questions.q3'),
    answer: t('faq.answers.a3')
  },
  {
    question: t('faq.questions.q4'),
    answer: t('faq.answers.a4')
  },
  {
    question: t('faq.questions.q5'),
    answer: t('faq.answers.a5')
  },
  {
    question: t('faq.questions.q6'),
    answer: t('faq.answers.a6')
  }
]);
</script>

<style scoped>
.faq-page {
  background-color: #f9f9f9;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding-bottom: 4rem;
}

.world-map-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background-image: url('/img/world-map-dots.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  opacity: 0.05;
  z-index: 0;
  pointer-events: none;
}

.page-header {
  padding-top: 2rem;
  animation: fadeIn 1s ease;
  position: relative;
  z-index: 1;
}

.global-badge {
  display: inline-flex;
  align-items: center;
  background-color: rgb(var(--v-theme-primary));
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-weight: 500;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
}

.globe-icon {
  margin-right: 0.5rem;
  animation: rotate 10s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.page-title {
  color: rgb(var(--v-theme-primary));
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.subtitle {
  font-size: 1.2rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 300;
  margin-top: 1rem;
}

.divider-primary {
  border-color: rgb(var(--v-theme-primary));
  border-width: 3px;
  opacity: 0.7;
}

.faq-panels {
  position: relative;
  z-index: 1;
}

.faq-panel {
  animation: fadeInUp 0.6s ease both;
  animation-delay: var(--animation-delay);
  transition: all 0.3s ease;
  overflow: hidden;
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
}

.faq-panel:hover {
  border-color: rgba(var(--v-theme-primary), 0.3);
  background-color: var(--hover-color);
}

.panel-title-content {
  position: relative;
}

.icon-wrapper {
  position: relative;
  width: 24px;
  height: 24px;
}

.question-icon {
  position: absolute;
  top: 0;
  left: 0;
  transition: all 0.3s ease;
}

.answer-icon {
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.3s ease;
}

.v-expansion-panel--active .question-icon {
  opacity: 0;
  transform: scale(0.5);
}

.v-expansion-panel--active .answer-icon {
  opacity: 1;
  transform: scale(1);
}

.panel-title {
  color: #333;
  transition: color 0.3s ease;
}

.v-expansion-panel--active .panel-title {
  color: rgb(var(--v-theme-primary));
}

.answer-content {
  padding-top: 0.5rem;
}

.answer-wrapper {
  border-left: 3px solid rgb(var(--v-theme-primary));
  padding-left: 1rem;
  margin-left: 2rem;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 26px !important;
  }
  
  .subtitle {
    font-size: 1rem;
  }
  
  .panel-title {
    font-size: 0.95rem !important;
  }
  
  .answer-wrapper {
    margin-left: 0.5rem;
  }
}
</style> 